
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useEntityRealtime, EntityRealtimeOptions } from '../core/useEntityRealtime';
import { SocketEvent } from '@scrambl/shared';
// Debug mode flag
const isBrowser = typeof window !== 'undefined' && typeof localStorage !== 'undefined';
const DEBUG_MODE = import.meta.env.DEV &&
  isBrowser &&
  (localStorage.getItem('REALTIME_DEBUG') === 'true' || localStorage.getItem('DEBUG_ALL') === 'true');

// Helper functions for conditional logging
function debugLog(..._args: unknown[]) {
  // Debug logging disabled - enable REALTIME_DEBUG in localStorage
}

export interface ProjectUpdate {
  id: string;
  name: string;
  description?: string;
  status: string;
  settings: Record<string, unknown>;
}

export interface UseProjectRealtimeOptions extends EntityRealtimeOptions {
  onProjectUpdated?: (project: ProjectUpdate) => void;
  onProjectDeleted?: (data: { id: string }) => void;
  onProjectStatusUpdated?: (data: { projectId: string, status: string }) => void;
  onProjectMemberAdded?: (data: { projectId: string, userId: string }) => void;
  onProjectMemberRemoved?: (data: { projectId: string, userId: string }) => void;
  // Task events that happen within this project
  onTaskCreated?: (task: any) => void;
  onTaskUpdated?: (task: any) => void;
  onTaskDeleted?: (data: { id: string }) => void;
  onTaskStatusUpdated?: (data: { taskId: string; statusId: string }) => void;
}

interface ProjectMemberEvent {
  projectId: string;
  userId: string;
}

// Type guards
function isProjectUpdate(data: unknown): data is ProjectUpdate {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.id === 'string' &&
    typeof d.name === 'string' &&
    typeof d.status === 'string' &&
    typeof d.settings === 'object' &&
    d.settings !== null
  );
}

function isProjectMemberEvent(data: unknown): data is ProjectMemberEvent {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.projectId === 'string' &&
    typeof d.userId === 'string'
  );
}

/**
 * Hook for real-time project updates
 * Uses the entity realtime hook for common functionality (socket messages)
 */
export function useProjectRealtime(projectId: string | null | undefined, options: UseProjectRealtimeOptions = {}) {
  // Keep track of options to avoid stale closures
  const optionsRef = useRef(options);
  
  // For debugging - track active instances
  const instanceId = useRef(`project-${Math.random().toString(36).substring(2, 8)}`);
  
  // Update options ref when options change (without causing re-renders)
  useEffect(() => {
    optionsRef.current = options;
  });
  
  // Memoize entity options to prevent re-renders
  const entityOptions = useMemo<EntityRealtimeOptions>(() => ({
    onEntityUpdated: (data: Record<string, unknown>) => {
      if (isProjectUpdate(data) && optionsRef.current.onProjectUpdated) {
        optionsRef.current.onProjectUpdated(data);
      }
    },
    onEntityDeleted: (data: Record<string, unknown>) => {
      if (optionsRef.current.onProjectDeleted) {
        optionsRef.current.onProjectDeleted(data as { id: string });
      }
    },
    // Override event names to use the project-specific socket events
    events: {
      created: SocketEvent.PROJECT_CREATED,
      updated: SocketEvent.PROJECT_UPDATED,
      deleted: SocketEvent.PROJECT_DELETED
    },
    suppressLogging: optionsRef.current.suppressLogging,
    throttleEvents: optionsRef.current.throttleEvents
  }), []);
  
  // Use the entity hook with project type
  const {
    isConnected,
    activeConnections,
    addEventListener,
    socket,
    markEventProcessed,
    hasProcessedEvent
  } = useEntityRealtime('project', projectId, entityOptions);
  
  // Project update handler
  const handleProjectUpdated = useCallback((data: unknown) => {
    if (!projectId || !isProjectUpdate(data) || data.id !== projectId) return;
    
    // Create unique event ID
    const eventId = `project-updated-${data.id}-${Date.now()}`;
    
    // Skip if already processed
    if (hasProcessedEvent(eventId)) {
      debugLog(`[useProjectRealtime:${instanceId.current}] Ignoring project update - already processed`);
      return;
    }
    
    // Mark as processed
    markEventProcessed(eventId);
    
    debugLog(`[useProjectRealtime:${instanceId.current}] Processing project update for project ${projectId}`);
    
    // Call callback if provided
    if (optionsRef.current.onProjectUpdated) {
      optionsRef.current.onProjectUpdated(data);
    }
  }, [projectId, hasProcessedEvent, markEventProcessed]);
  
  // Project deleted handler
  const handleProjectDeleted = useCallback((data: unknown) => {
    if (!projectId || !data || typeof data !== 'object') return;
    const d = data as { id: string };
    if (d.id !== projectId) return;
    
    // Create unique event ID
    const eventId = `project-deleted-${d.id}-${Date.now()}`;
    
    // Skip if already processed
    if (hasProcessedEvent(eventId)) {
      debugLog(`[useProjectRealtime:${instanceId.current}] Ignoring project deletion - already processed`);
      return;
    }
    
    // Mark as processed
    markEventProcessed(eventId);
    
    debugLog(`[useProjectRealtime:${instanceId.current}] Processing project deletion for project ${projectId}`);
    
    // Call callback if provided
    if (optionsRef.current.onProjectDeleted) {
      optionsRef.current.onProjectDeleted(d);
    }
  }, [projectId, hasProcessedEvent, markEventProcessed]);
  
  // Project status update handler
  const handleProjectStatusUpdated = useCallback((data: unknown) => {
    if (!projectId || !data || typeof data !== 'object') return;
    const d = data as { projectId: string, status: string };
    if (d.projectId !== projectId) return;
    
    // Create unique event ID
    const eventId = `project-status-${d.projectId}-${d.status}-${Date.now()}`;
    
    // Skip if already processed
    if (hasProcessedEvent(eventId)) {
      debugLog(`[useProjectRealtime:${instanceId.current}] Ignoring status update - already processed`);
      return;
    }
    
    // Mark as processed
    markEventProcessed(eventId);
    
    debugLog(`[useProjectRealtime:${instanceId.current}] Processing project status update for project ${projectId}`);
    
    // Call callback if provided
    if (optionsRef.current.onProjectStatusUpdated) {
      optionsRef.current.onProjectStatusUpdated(d);
    }
  }, [projectId, hasProcessedEvent, markEventProcessed]);
  
  // Project member added event handler
  const handleMemberAdded = useCallback((data: unknown) => {
    if (!projectId || !isProjectMemberEvent(data) || data.projectId !== projectId) return;
    
    // Create unique event ID
    const eventId = `project-member-added-${data.projectId}-${data.userId}-${Date.now()}`;
    
    // Skip if already processed
    if (hasProcessedEvent(eventId)) {
      debugLog(`[useProjectRealtime:${instanceId.current}] Ignoring member added - already processed`);
      return;
    }
    
    // Mark as processed
    markEventProcessed(eventId);
    
    debugLog(`[useProjectRealtime:${instanceId.current}] Processing member added for project ${projectId}`);
    
    // Call callback if provided
    if (optionsRef.current.onProjectMemberAdded) {
      optionsRef.current.onProjectMemberAdded(data);
    }
  }, [projectId, hasProcessedEvent, markEventProcessed]);
  
  // Project member removed event handler
  const handleMemberRemoved = useCallback((data: unknown) => {
    if (!projectId || !isProjectMemberEvent(data) || data.projectId !== projectId) return;
    
    // Create unique event ID
    const eventId = `project-member-removed-${data.projectId}-${data.userId}-${Date.now()}`;
    
    // Skip if already processed
    if (hasProcessedEvent(eventId)) {
      debugLog(`[useProjectRealtime:${instanceId.current}] Ignoring member removed - already processed`);
      return;
    }
    
    // Mark as processed
    markEventProcessed(eventId);
    
    debugLog(`[useProjectRealtime:${instanceId.current}] Processing member removed for project ${projectId}`);
    
    // Call callback if provided
    if (optionsRef.current.onProjectMemberRemoved) {
      optionsRef.current.onProjectMemberRemoved(data);
    }
  }, [projectId, hasProcessedEvent, markEventProcessed]);
  
  // Task event handlers
  const handleTaskCreated = useCallback((data: any) => {
    debugLog(`[useProjectRealtime] Raw task:created event received:`, data);
    if (!projectId || !data) return;
    
    // Only handle tasks that belong to this project
    if (data.moduleType === 'PROJECT' && data.moduleId === projectId) {
      debugLog(`[useProjectRealtime:${instanceId.current}] Task created in project ${projectId}:`, data.id);
      optionsRef.current.onTaskCreated?.(data);
    }
  }, [projectId]);

  const handleTaskUpdated = useCallback((data: any) => {
    debugLog(`[useProjectRealtime] Raw task:updated event received:`, data);
    if (!projectId || !data) return;
    
    // Only handle tasks that belong to this project
    if (data.moduleType === 'PROJECT' && data.moduleId === projectId) {
      debugLog(`[useProjectRealtime:${instanceId.current}] Task updated in project ${projectId}:`, data.id);
      optionsRef.current.onTaskUpdated?.(data);
    }
  }, [projectId]);

  const handleTaskDeleted = useCallback((data: any) => {
    debugLog(`[useProjectRealtime] Raw task:deleted event received:`, data);
    if (!projectId || !data) return;
    
    debugLog(`[useProjectRealtime:${instanceId.current}] Task deleted in project ${projectId}:`, data.id);
    optionsRef.current.onTaskDeleted?.(data);
  }, [projectId]);

  const handleTaskStatusUpdated = useCallback((data: any) => {
    debugLog(`[useProjectRealtime] Raw task:status_updated event received:`, data);
    if (!projectId || !data) return;
    
    // Only handle tasks that belong to this project
    if (data.moduleType === 'PROJECT' && data.moduleId === projectId) {
      debugLog(`[useProjectRealtime:${instanceId.current}] Task status updated in project ${projectId}:`, data.taskId, 'to status:', data.statusId);
      optionsRef.current.onTaskStatusUpdated?.(data);
    }
  }, [projectId]);

  // Set up project-specific socket message handlers using standard useEffect
  useEffect(() => {
    if (!socket || !projectId) return;
    
    debugLog(`[useProjectRealtime:${instanceId.current}] Setting up message handlers for project: ${projectId}`);
    
    // Add a debug listener to see ALL messages
    const allMessagesListener = (...args: any[]) => {
      debugLog('[useProjectRealtime] Received message:', args);
    };
    
    // Listen to all messages for debugging
    socket.onAny(allMessagesListener);
    
    // Set up event listeners using addEventListener from useEntityRealtime
    // This will handle broadcasting to other tabs automatically
    const cleanupProjectUpdated = addEventListener(SocketEvent.PROJECT_UPDATED, handleProjectUpdated);
    const cleanupProjectDeleted = addEventListener(SocketEvent.PROJECT_DELETED, handleProjectDeleted);
    const cleanupStatusUpdated = addEventListener(SocketEvent.PROJECT_STATUS_UPDATED, handleProjectStatusUpdated);
    const cleanupMemberAdded = addEventListener(SocketEvent.PROJECT_MEMBER_ADDED, handleMemberAdded);
    const cleanupMemberRemoved = addEventListener(SocketEvent.PROJECT_MEMBER_REMOVED, handleMemberRemoved);
    
    // Also listen for task events in the project room
    const cleanupTaskCreated = addEventListener(SocketEvent.TASK_CREATED, handleTaskCreated);
    const cleanupTaskUpdated = addEventListener(SocketEvent.TASK_UPDATED, handleTaskUpdated);
    const cleanupTaskDeleted = addEventListener(SocketEvent.TASK_DELETED, handleTaskDeleted);
    const cleanupTaskStatusUpdated = addEventListener(SocketEvent.TASK_STATUS_UPDATED, handleTaskStatusUpdated);
    
    // Return cleanup function
    return () => {
      debugLog(`[useProjectRealtime:${instanceId.current}] Cleaning up message handlers for project: ${projectId}`);
      socket.offAny(allMessagesListener);
      cleanupProjectUpdated();
      cleanupProjectDeleted();
      cleanupStatusUpdated();
      cleanupMemberAdded();
      cleanupMemberRemoved();
      cleanupTaskCreated();
      cleanupTaskUpdated();
      cleanupTaskDeleted();
      cleanupTaskStatusUpdated();
    };
  }, [
    socket, 
    projectId, 
    addEventListener,
    handleProjectUpdated,
    handleProjectDeleted,
    handleProjectStatusUpdated,
    handleMemberAdded,
    handleMemberRemoved,
    handleTaskCreated,
    handleTaskUpdated,
    handleTaskDeleted,
    handleTaskStatusUpdated
  ]);
  
  return {
    isConnected,
    activeConnections,
    debug: {
      enableDebug: () => {
        localStorage.setItem('REALTIME_DEBUG', 'true');
      },
      disableDebug: () => {
        localStorage.removeItem('REALTIME_DEBUG');
      },
      projectId,
      instanceId: instanceId.current,
      isDebugEnabled: DEBUG_MODE
    }
  };
}